import { Autocomplete, DropdownItem } from '@dev-spendesk/grapes';
import React, { type FocusEventHandler, useMemo } from 'react';

import { AutocompleteNoOptions } from 'common/components/AutocompleteNoOptions';
import { useDebouncedState } from 'common/hooks/useDebounceState';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { type PurchaseOrder } from '../../../models';

export type PurchaseOrderAutocompleteSearchProps = {
  purchaseOrders: PurchaseOrder[];
  placeholder: string;
  noPurchaseOrderPlaceholder: string;
  className?: string;
  fit?: 'content' | 'parent';
  id?: string;
  isInvalid?: boolean;
  isForcedDisabled?: boolean;
  name?: string;
  value: PurchaseOrder | undefined | null;
  showClearSelectionButton?: boolean;
  onSelect(value: PurchaseOrder | undefined): void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  inputVariant?: 'magicGradient' | 'default';
  onSearch: (value?: string) => void;
  isLoading?: boolean;
};

export const PurchaseOrderAutocompleteSearch = ({
  onSelect,
  purchaseOrders,
  value,
  noPurchaseOrderPlaceholder,
  placeholder,
  isForcedDisabled = false,
  isLoading,
  onSearch,
  ...props
}: PurchaseOrderAutocompleteSearchProps) => {
  const [search, setSearch, { isDebouncing }] = useDebouncedState<string>(
    '',
    500,
    {
      onValueChange: onSearch,
    },
  );

  const isDisabled =
    (purchaseOrders.length === 0 && search === '') || isForcedDisabled;

  const options = useMemo(
    () =>
      purchaseOrders.map((purchaseOrder) => ({
        key: purchaseOrder.id,
        label: getAutocompleteLabel(purchaseOrder),
      })),
    [purchaseOrders],
  );

  return (
    <Autocomplete
      {...props}
      isLoading={isLoading || isDebouncing}
      onSearch={(searchValue) => {
        if (searchValue) {
          setSearch(searchValue);
        }
      }}
      isDisabled={isDisabled}
      placeholder={
        isDisabled && purchaseOrders.length === 0
          ? noPurchaseOrderPlaceholder
          : placeholder
      }
      value={
        value
          ? {
              key: value.id,
              label: getAutocompleteLabel(value),
            }
          : undefined
      }
      onSelect={(newValue) =>
        onSelect(
          purchaseOrders.find(
            (purchaseOrder) => purchaseOrder.id === newValue?.key,
          ),
        )
      }
      options={options}
      renderOption={(purchaseOrder, state) => (
        <DropdownItem label={purchaseOrder.label} {...state} />
      )}
      renderNoOptions={(rawValue) => <AutocompleteNoOptions value={rawValue} />}
    />
  );
};

const getAutocompleteLabel = (purchaseOrder: PurchaseOrder): string => {
  try {
    return `${purchaseOrder.purchaseOrderNumber} | ${
      purchaseOrder.supplier.name
    } | ${formatMonetaryValue(purchaseOrder.amount)}`;
  } catch {
    return purchaseOrder.purchaseOrderNumber;
  }
};
